import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { MessageCircle, Send, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ReplyPage() {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchMessage();
  }, [messageId, user]);

  const fetchMessage = async () => {
    try {
      // Fetch message details
      const response = await api.getMessage(messageId);
      setMessage(response.data);
    } catch (error) {
      console.error('Failed to fetch message:', error);
      toast.error('Failed to load message');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!replyContent.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    setSending(true);
    try {
      await api.sendReplyToMessage(message.qr_code_id, message.id, {
        content: replyContent
      });
      
      toast.success('Reply sent successfully!');
      setReplyContent('');
      
      // Redirect to dashboard after a moment
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error('Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <div className="animate-spin w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading message...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-2xl border-0">
          <CardHeader className="text-center">
            <CardTitle className="text-red-600">Message Not Found</CardTitle>
            <CardDescription>This message could not be found or you don't have permission to reply.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                iComment.app
              </span>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="border-blue-200 hover:bg-blue-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Reply Form */}
      <div className="flex items-center justify-center p-4 pt-12">
        <Card className="w-full max-w-2xl shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Send className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Reply to Message
            </CardTitle>
            <CardDescription className="text-gray-600 mt-2">
              About: <span className="font-semibold">{message.qr_title || 'Your QR Code'}</span>
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Original Message */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-5 rounded-xl border-2 border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <MessageCircle className="w-4 h-4 mr-2 text-blue-600" />
                Original Message:
              </h3>
              <p className="text-gray-800 leading-relaxed">{message.content}</p>
              <p className="text-xs text-gray-500 mt-3">
                Received: {new Date(message.created_at).toLocaleString()}
              </p>
            </div>

            {/* Contact Info Note */}
            {(message.sender_email || message.sender_phone) ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  ✅ The finder provided contact information. Your reply will be delivered to them through iComment.app.
                </p>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  ⚠️ This was an anonymous message. Your reply will be sent through the platform, but the finder may not receive it if they didn't provide contact information.
                </p>
              </div>
            )}

            {/* Reply Form */}
            <form onSubmit={handleSendReply} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Your Reply:</label>
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Type your reply here..."
                  rows={6}
                  className="border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
                  required
                />
              </div>

              <Button 
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-6 text-lg transform hover:scale-105 transition-all duration-200 shadow-lg"
                disabled={sending || !replyContent.trim()}
              >
                {sending ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Sending Reply...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Send className="w-5 h-5 mr-2" />
                    Send Reply
                  </span>
                )}
              </Button>
            </form>

            <div className="text-xs text-gray-600 text-center pt-4 border-t border-gray-200">
              <p className="flex items-center justify-center">
                <span className="text-lg mr-2">🔒</span>
                Your reply will be sent securely through iComment.app
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
